import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);

  const [userEmail, setUserEmail] = useState(null);
  const [userName, setUserName] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const [userPlan, setUserPlan] = useState('free');
  const [hasAccess, setHasAccess] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);
  const [accessSource, setAccessSource] = useState(null);
  const [freeUntil, setFreeUntil] = useState(null);
  const [premiumUntil, setPremiumUntil] = useState(null);

  const [loadingAuth, setLoadingAuth] = useState(true);

  const [clusters, setClusters] = useState([]);
  const [activeCluster, setActiveCluster] = useState(null);

  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const isLoggedIn = Boolean(token && userEmail);

  const isAdmin = userRole === 'admin';
  const isAccessExpired = isLoggedIn && hasAccess === false;

  /**
   * Ambil identitas user dari server.
   * /api/me adalah sumber kebenaran untuk:
   * - email/name/role
   * - plan
   * - has_access/access_status
   * - free_until/premium_until
   * - clusters
   */
  const refreshProfile = useCallback(async (tokenOverride) => {
    const t = tokenOverride || localStorage.getItem('token') || token;
    if (!t) return null;

    try {
      const res = await fetch('/api/me', {
        headers: { Authorization: `Bearer ${t}` },
        credentials: 'include',
      });

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error('unauthorized');
        }
        throw new Error(`GET /api/me ${res.status}`);
      }

      const me = await res.json();

      setUserEmail(me.email || '');
      setUserName(me.name || '');
      setUserRole(me.system_role || me.role || 'user');

      setUserPlan(me.plan || me.user_plan || 'free');
      setHasAccess(me.has_access !== false);
      setAccessStatus(me.access_status || null);
      setAccessSource(me.access_source || null);
      setFreeUntil(me.free_until || null);
      setPremiumUntil(me.premium_until || null);

      setClusters(Array.isArray(me.clusters) ? me.clusters : []);

      if (me.active_cluster_id) {
        setActiveCluster((prev) => {
          if (prev && prev.id === me.active_cluster_id) return prev;

          const foundCluster = Array.isArray(me.clusters)
            ? me.clusters.find((c) => c.id === me.active_cluster_id)
            : null;

          return foundCluster || prev;
        });
      }

      return me;
    } catch (err) {
      console.error('refreshProfile error:', err);

      setUserEmail(null);
      setUserName(null);
      setUserRole(null);
      setUserPlan('free');
      setHasAccess(true);
      setAccessStatus(null);
      setAccessSource(null);
      setFreeUntil(null);
      setPremiumUntil(null);
      setClusters([]);
      setActiveCluster(null);

      throw err;
    }
  }, [token]);

  /**
   * Logout: hapus hanya key yang dipakai Saham Vista.
   */
  const handleLogout = useCallback(() => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('ui:layout');
      localStorage.removeItem('ui:sidebarOpen');
      localStorage.removeItem('activeClusterId');
      localStorage.removeItem('activeClusterName');
      localStorage.removeItem('activeChatPublicId');
    } catch (e) {
      console.warn('localStorage cleanup error:', e);
    }

    setToken(null);

    setUserEmail(null);
    setUserName(null);
    setUserRole(null);

    setUserPlan('free');
    setHasAccess(true);
    setAccessStatus(null);
    setAccessSource(null);
    setFreeUntil(null);
    setPremiumUntil(null);

    setClusters([]);
    setActiveCluster(null);
  }, []);

  /**
   * Dipanggil setelah Google login berhasil dan backend return token aplikasi.
   */
  const handleLogin = useCallback(async (newToken) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);

    try {
      await refreshProfile(newToken);
    } catch (e) {
      console.warn('Login token invalid after set. Logging out.', e);
      handleLogout();
    }
  }, [refreshProfile, handleLogout]);

  /**
   * Bootstrap saat aplikasi pertama load:
   * - token invalid / 401 => logout
   * - token valid + has_access true => login normal
   * - token valid + has_access false => tetap login, app nanti dikunci oleh MainApplication/AppShell
   */
  useEffect(() => {
    const bootstrap = async () => {
      const storedToken = localStorage.getItem('token');

      if (!storedToken) {
        setLoadingAuth(false);
        return;
      }

      setToken(storedToken);

      try {
        const res = await fetch('/api/check-session', {
          headers: { Authorization: `Bearer ${storedToken}` },
          credentials: 'include',
        });

        if (!res.ok) {
          if (res.status === 401) {
            throw new Error('unauthorized');
          }
          throw new Error(`GET /api/check-session ${res.status}`);
        }

        const session = await res.json();

        // Optional: simpan data cepat dari check-session.
        // Nanti tetap disempurnakan oleh /api/me.
        setUserEmail(session.email || '');
        setUserName(session.name || '');
        setUserRole(session.system_role || session.role || 'user');

        setUserPlan(session.plan || 'free');
        setHasAccess(session.has_access !== false);
        setAccessStatus(session.access_status || null);
        setAccessSource(session.access_source || null);
        setFreeUntil(session.free_until || null);
        setPremiumUntil(session.premium_until || null);

        await refreshProfile(storedToken);
      } catch (e) {
        console.warn('🔒 Session invalid. Logging out.', e);
        handleLogout();
      } finally {
        setLoadingAuth(false);
      }
    };

    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Kalau token berubah tapi profile belum terisi, tarik profile ulang.
   */
  useEffect(() => {
    if (token && !userEmail) {
      refreshProfile(token).catch((e) => {
        console.warn('Failed to refresh profile after token change:', e);
      });
    }
  }, [token, userEmail, refreshProfile]);

  const authContextValue = {
    token,

    userEmail,
    userName,
    userRole,

    userPlan,
    hasAccess,
    accessStatus,
    accessSource,
    freeUntil,
    premiumUntil,

    isAdmin,
    isAccessExpired,

    loadingAuth,
    isLoggedIn,

    handleLogin,
    handleLogout,
    refreshProfile,

    clusters,
    setClusters,
    activeCluster,
    setActiveCluster,
  };

  if (!GOOGLE_CLIENT_ID) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">
        <div className="bg-red-800 p-6 rounded shadow-md text-center">
          <h2 className="text-2xl font-bold mb-4">Configuration Error</h2>
          <p>Google login is not properly configured. Please check your .env file.</p>
        </div>
      </div>
    );
  }

  if (loadingAuth) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">
        <p>Verifying session...</p>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={authContextValue}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        {children}
      </GoogleOAuthProvider>
    </AuthContext.Provider>
  );
};

export default AuthProvider;
